import { useYardStores } from '@yard/AppProvider'
import { AllocationRuleStore } from '@yard/stores/allocation-rule.store'
import { JourneyStore } from '@yard/stores/journey.store'
import { OccupancyStore } from '@yard/stores/OccupancyStore'
import { YardBlockStore } from '@yard/stores/YardBlockStore'
import {
  getBlockCapacity,
  getRuleCapacity,
  getYardCapacity,
  RuleCapacity,
  ScopeCapacity,
} from '@yard/utils/allocation-rule-capacity.util'
import { aggregateJourneysByRules } from '@yard/utils/journey.util'
import { YardIssueDto, YardIssueTypeDummy } from '../models/yard-issue.model'

export type CapacityContext =
  | { kind: 'rule'; name: string; ruleType: 'global' | 'carrierVisit'; capacity: RuleCapacity }
  | { kind: 'block'; name: string; capacity: ScopeCapacity }
  | { kind: 'yard'; capacity: ScopeCapacity }

export interface YardIssueCapacityStores {
  allocationRuleStore: AllocationRuleStore
  yardBlockStore: YardBlockStore
  occupancyStore: OccupancyStore
  journeyStore: JourneyStore
}

export const computeYardIssueCapacityContext = (
  issue: YardIssueDto,
  stores: YardIssueCapacityStores,
): CapacityContext | null => {
  const { allocationRuleStore, yardBlockStore, occupancyStore, journeyStore } = stores

  if (issue.issueType === YardIssueTypeDummy.YardCapacityFull) {
    return {
      kind: 'yard',
      capacity: getYardCapacity(yardBlockStore.items, occupancyStore.occupanciesByBlock),
    }
  }

  if (issue.issueType === YardIssueTypeDummy.BlockCapacityFull) {
    const block = yardBlockStore.items.find(b => b.id === issue.blockId)
    if (!block) return null
    return {
      kind: 'block',
      name: block.name,
      capacity: getBlockCapacity(block, occupancyStore.occupanciesByBlock[block.id] ?? []),
    }
  }

  if (issue.issueType === YardIssueTypeDummy.AllocationRuleCapacityFull) {
    if (!issue.allocationRuleId) return null
    const rule = allocationRuleStore.items.find(r => r.id === issue.allocationRuleId)
    if (!rule) return null
    const journeysByRuleId = aggregateJourneysByRules(journeyStore.items, [
      ...allocationRuleStore.globalRules,
      ...allocationRuleStore.carrierVisitRules,
    ])
    return {
      kind: 'rule',
      name: rule.name,
      ruleType: rule.facets?.carrierVisitId ? 'carrierVisit' : 'global',
      capacity: getRuleCapacity(rule, {
        yardBlocks: yardBlockStore.items,
        occupanciesByBlock: occupancyStore.occupanciesByBlock,
        journeysByRuleId,
      }),
    }
  }

  return null
}

export const useYardIssueCapacityContext = (issue: YardIssueDto): CapacityContext | null => {
  const { allocationRuleStore, yardBlockStore, occupancyStore, journeyStore } = useYardStores()
  return computeYardIssueCapacityContext(issue, {
    allocationRuleStore,
    yardBlockStore,
    occupancyStore,
    journeyStore,
  })
}
